'use client';

import useSWR from 'swr';
import { accountsApi, itemsApi, type ItemWithAccounts } from '@/lib/api';
import { VOLATILE_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/**
 * `/connections` sometimes serializes rows before their accounts are embedded — the
 * existing connection surfaces (Connections page, platform onboarding gate)
 * enrich from `/accounts` when EVERY item comes back bare, and consumers of this
 * hook rely on `accounts.length` to judge a connection usable. Mirror that.
 */
async function loadItemsWithAccounts(token: string): Promise<ItemWithAccounts[]> {
  const items = await itemsApi.getAll(token);
  if (items.length === 0 || items.some((item) => item.accounts.length > 0)) {
    return items;
  }

  try {
    const accounts = await accountsApi.getAll(token);
    const accountsByItem = new Map<string, ItemWithAccounts['accounts']>();
    for (const account of accounts) {
      const list = accountsByItem.get(account.itemId) ?? [];
      list.push(account);
      accountsByItem.set(account.itemId, list);
    }
    return items.map((item) => ({
      ...item,
      accounts: accountsByItem.get(item.id) ?? item.accounts,
    }));
  } catch (err) {
    console.warn('Failed to enrich items with accounts:', err);
    return items;
  }
}

/** The user's bank connections with embedded accounts (`/connections`). Pass
 * `refreshMs` to poll — the onboarding import watcher does. */
export function useItems(refreshMs?: number) {
  const authedFetch = useAuthedFetcher();

  return useSWR<ItemWithAccounts[]>(
    ['items'],
    () => authedFetch((token) => loadItemsWithAccounts(token)),
    { ...VOLATILE_SWR_OPTIONS, refreshInterval: refreshMs ?? 0 },
  );
}
